import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import { BRAND } from "@/lib/brand";
import { POSTS, BLOG_CATEGORIES } from "@/lib/blog";
import { ArrowRight, Clock, Search } from "lucide-react";

export default function Blog() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");

  useEffect(() => { document.title = `Blog — ${BRAND.name}`; }, []);

  const filtered = useMemo(() => {
    return POSTS.filter((p) => {
      const matchCat = cat === "All" || p.category === cat;
      const matchQ = !q.trim() || (p.title + p.excerpt + p.category).toLowerCase().includes(q.toLowerCase());
      return matchCat && matchQ;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [q, cat]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SiteHeader />

      <section className="max-w-[1280px] mx-auto px-5 md:px-8 py-16">
        <div className="max-w-3xl">
          <div className="eyebrow">Knowledge Base</div>
          <h1 className="font-heading font-bold text-4xl md:text-6xl mt-4 leading-[1.05]">
            The <span className="text-brand-gradient">Claim Playbook.</span>
          </h1>
          <p className="mt-5 text-slate-600 text-lg leading-relaxed">
            Practical, plain-English guides on insurance claims in India — written by the team that handles them every day.
          </p>
        </div>

        {/* Filter row */}
        <div className="mt-10 flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            {(["All", ...BLOG_CATEGORIES]).map((c) => (
              <button
                key={c}
                data-testid={`blog-cat-${c.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                onClick={() => setCat(c)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  cat === c ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-navy"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="blog-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search articles…"
              className="w-full pl-10 pr-4 py-2.5 rounded-full border border-slate-200 bg-white text-sm focus:outline-none focus:border-navy"
            />
          </div>
        </div>

        {/* Featured post */}
        {featured && (
          <Link to={`/blog/${featured.slug}`} data-testid={`blog-featured-${featured.slug}`} className="block mt-10 brand-card overflow-hidden md:grid md:grid-cols-2">
            <div className="aspect-[16/10] md:aspect-auto">
              <img src={featured.cover} alt={featured.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-8 md:p-10 flex flex-col justify-center">
              <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-brand font-semibold">{featured.category}</div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl mt-3 leading-tight">{featured.title}</h2>
              <p className="mt-3 text-slate-600 leading-relaxed">{featured.excerpt}</p>
              <div className="mt-5 flex items-center justify-between text-xs text-slate-500">
                <span>{new Date(featured.date).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "2-digit" })}</span>
                <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {featured.readTime}</span>
              </div>
              <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-emerald-brand">
                Read article <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </Link>
        )}

        {/* Grid */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((p) => (
            <Link to={`/blog/${p.slug}`} key={p.slug} data-testid={`blog-card-${p.slug}`} className="brand-card overflow-hidden flex flex-col">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={p.cover} alt={p.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
              </div>
              <div className="p-6 flex flex-col gap-3 flex-1">
                <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-brand font-semibold">{p.category}</div>
                <h3 className="font-heading font-bold text-lg leading-snug">{p.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">{p.excerpt}</p>
                <div className="mt-auto pt-4 flex items-center justify-between text-xs text-slate-500 border-t border-slate-100">
                  <span>{new Date(p.date).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "2-digit" })}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {p.readTime}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="brand-card p-12 text-center mt-10">
            <div className="font-heading font-semibold text-lg">No articles match your search</div>
            <p className="text-sm text-slate-500 mt-2">Try clearing the filter or searching another term.</p>
          </div>
        )}
      </section>

      <SiteFooter />
      <WhatsAppFAB />
    </div>
  );
}
